import { Building2, Leaf, CheckCircle, ArrowLeft, Sun, Download, TrendingUp, Battery, Home, LineChart, HelpCircle, FileText } from 'lucide-react';
import type { SolarDesign, FinancialData, LocationData, UserType } from '../types';
import { Footer } from './Footer';
import { generatePDFReport } from '../services/reportGenerator';
import { generateCalculationSourcesPDF, type CalculationSourcesData } from '../services/calculationSourcesReport';
import { findNearestCity } from '../data/saudiWeatherData';
import { BOQDisplay } from './BOQDisplay';
import type { BOQSummary } from '../services/boqCalculationService';
import { getEffectiveConsumption } from '../utils/consumptionNormalization';
import { BrandLogo } from './BrandLogo';
import { useAuthStore } from '../store/authStore';
import { uploadPdfAndRecord } from '../services/projectDocuments';

interface ProjectSummaryProps {
  location: LocationData;
  design?: SolarDesign;
  financials?: FinancialData;
  boq?: BOQSummary;
  financing?: any;
  billData?: any;
  contactData?: any;
  energyConsumptionData?: any;
  siteOperationsData?: any;
  agriDesignData?: any;
  userType?: UserType | null;
  persistPdfProjectId?: string | null;
  persistPdfDesignId?: string | null;
  onConfirm?: () => void;
  onBack?: () => void;
}

export function ProjectSummary({
  location,
  design,
  financials,
  boq,
  billData,
  contactData,
  energyConsumptionData,
  siteOperationsData,
  agriDesignData,
  userType,
  persistPdfProjectId = null,
  persistPdfDesignId = null,
  onConfirm,
  onBack,
}: ProjectSummaryProps) {
  const { user } = useAuthStore();
  const formatSar = (amount: number) =>
    new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const InfoTip = ({ text }: { text: string }) => (
    <span
      className="inline-flex items-center ml-1 text-gray-400 hover:text-gray-600 cursor-help"
      title={text}
    >
      <HelpCircle className="h-4 w-4" />
    </span>
  );

  const hasMonthlyConsumptionArray =
    billData?.monthlyConsumptionArray &&
    Array.isArray(billData.monthlyConsumptionArray) &&
    billData.monthlyConsumptionArray.length === 12 &&
    billData.monthlyConsumptionArray.some((v: unknown) => typeof v === 'number' && v > 0);

  const effectiveConsumption = getEffectiveConsumption(billData);

  const yearlyConsumptionKwh = hasMonthlyConsumptionArray
    ? (billData.monthlyConsumptionArray as number[]).reduce(
        (sum: number, v: number) => sum + (Number.isFinite(v) ? v : 0),
        0
      )
    : billData?.yearlyConsumption ?? (billData?.monthlyUsage ? billData.monthlyUsage * 12 : 0);

  const avgMonthlyUsageKwh = hasMonthlyConsumptionArray
    ? yearlyConsumptionKwh / 12
    : billData?.monthlyUsage ?? 0;

  const displayedOffsetPct =
    design && effectiveConsumption.effectiveYearlyConsumption > 0
      ? Math.min(100, Math.round((design.yearlyProduction / effectiveConsumption.effectiveYearlyConsumption) * 100))
      : (design?.offset ?? 0);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Project Information Summary</h2>
              <p className="text-blue-100">Review your project details and requirements</p>
            </div>
            <div className="flex space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="bg-white/20 text-white px-6 py-3 rounded-lg font-medium flex items-center hover:bg-white/30 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Solar Design + Financials (surface at top) */}
          {design && financials && (
            <div className="mb-10">
              <div className="flex items-center mb-6">
                <Sun className="h-6 w-6 text-orange-500 mr-2" />
                <h3 className="text-2xl font-semibold text-gray-800">Solar System Design</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
                  <p className="text-sm text-gray-600 mb-2 flex items-center">
                    System Size
                    <InfoTip text="The PV array size (kWp) required to meet the target offset based on your consumption and site conditions." />
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{design.systemSize.toFixed(1)}</p>
                  <p className="text-sm text-gray-600">kWp</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-6 border border-blue-100">
                  <p className="text-sm text-gray-600 mb-2 flex items-center">
                    Solar Panels
                    <InfoTip text="The estimated number of PV modules required for the selected system size and module power." />
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{design.panelCount}</p>
                  <p className="text-sm text-gray-600">panels</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <p className="text-sm text-gray-600 mb-2 flex items-center">
                    1st Year Production
                    <InfoTip text="Estimated energy generated in the first year (kWh/year), based on weather/location and system design assumptions." />
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{(design.yearlyProduction / 1000).toFixed(1)}</p>
                  <p className="text-sm text-gray-600">MWh/year</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                  <p className="text-sm text-gray-600 mb-2 flex items-center">
                    Energy Offset
                    <InfoTip text="The percent of your annual consumption that the PV system is expected to cover in the first year (capped at 100%)." />
                  </p>
                  <p className="text-3xl font-bold text-gray-900">{displayedOffsetPct}</p>
                  <p className="text-sm text-gray-600">%</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Financial Overview</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 flex items-center">
                      Total System Cost (BOQ incl. VAT)
                      <InfoTip text="This is the total upfront system cost from the BOQ (materials + labor + VAT). No subsidy is applied." />
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatSar((boq?.grandTotal ?? financials.totalCost) || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center">
                      Payback Period
                      <InfoTip text="Years until first-year savings recover the upfront cost, using simple payback: total cost / first-year savings." />
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{financials.paybackPeriod.toFixed(1)} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center">
                      1st Year Saving
                      <InfoTip text="Estimated electricity bill reduction during the first 12 months of system operation." />
                    </p>
                    <p className="text-2xl font-bold text-gray-900">{formatSar(financials.savingsYear1 || 0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center">
                      25-Year Savings (Gross)
                      <InfoTip text="Estimated cumulative bill reduction over 25 years, accounting for long-term performance assumptions." />
                    </p>
                    <p className="text-2xl font-bold text-green-600">{formatSar(financials.savings25Year || 0)}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <button
                  onClick={async () => {
                    try {
                      const cityData = findNearestCity(
                        location.locations?.[0]?.latitude || location.latitude || 24.7136,
                        location.locations?.[0]?.longitude || location.longitude || 46.6753
                      );
                      const projectNameBase = `Solar_Project_${new Date().toISOString().split('T')[0]}`;
                      const persistPdf = Boolean(persistPdfProjectId && user);

                      const blob = await generatePDFReport({
                        projectName: projectNameBase,
                        locationName: cityData.name,
                        latitude: location.locations?.[0]?.latitude || location.latitude || 24.7136,
                        longitude: location.locations?.[0]?.longitude || location.longitude || 46.6753,
                        altitude: cityData.altitude,
                        tilt: location.locations?.[0]?.tilt || location.tilt || 10,
                        azimuth: location.locations?.[0]?.azimuth || location.azimuth || 180,
                        mountingType: location.locations?.[0]?.mounting_type || location.mountingType || 'roof-fixed',
                        surfaceType: location.locations?.[0]?.surface_type || location.surfaceType || 'asphalt',
                        monthlyConsumption: billData?.monthlyConsumptionArray || Array(12).fill((billData?.yearlyConsumption || 12000) / 12),
                        systemPower: design.systemSize,
                        annualProduction: design.yearlyProduction,
                        specificProduction: design.yearlyProduction / design.systemSize,
                        performanceRatio: design.monthlyResults ? design.monthlyResults.reduce((sum, m) => sum + m.performanceRatio, 0) / 12 : 80,
                        monthlyResults: design.monthlyResults ? design.monthlyResults.map(m => ({
                          month: m.month,
                          globHor: m.ghi,
                          diffHor: m.ghi * 0.3,
                          tempAmb: m.temp,
                          globInc: m.poa,
                          globEff: m.poa,
                          eArray: m.arrayOutput,
                          eGrid: m.gridOutput,
                          pr: m.performanceRatio
                        })) : [],
                        systemConfig: {
                          modulesPerString: Math.ceil(design.panelCount / 2),
                          stringsParallel: 2,
                          nInverters:
                            (boq?.items.find((i) => i.category === 'Inverters')?.quantity as number | undefined) ?? 1,
                          modulePower: 550,
                          inverterPower: design.selectedInverterAcKw ?? (design.systemSize / 1.3),
                          totalModules: design.panelCount,
                          dcAcRatio: design.dcAcRatioFinal,
                        },
                        boq: boq || undefined
                      }, persistPdf ? { disposition: 'both' } : undefined);
                      if (blob instanceof Blob && persistPdfProjectId && user) {
                        await uploadPdfAndRecord({
                          projectId: persistPdfProjectId,
                          designId: persistPdfDesignId,
                          docType: 'solar_report_pdf',
                          blob,
                          storageFileBase: projectNameBase,
                          filesAppendType: 'solar_report_pdf',
                        });
                      }
                    } catch (error) {
                      console.error('PDF generation error:', error);
                      alert('Failed to generate PDF report. Please try again.');
                    }
                  }}
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-lg hover:from-orange-600 hover:to-red-600 flex items-center justify-center transition-all transform hover:scale-105 shadow-lg text-lg font-medium"
                >
                  <Download className="h-6 w-6 mr-3" />
                  Download Professional Report (PDF)
                </button>
                <button
                  onClick={async () => {
                    try {
                      const lat = location.locations?.[0]?.latitude ?? location.latitude ?? 24.7136;
                      const lon = location.locations?.[0]?.longitude ?? location.longitude ?? 46.6753;
                      const cityData = findNearestCity(lat, lon);
                      const effectiveConsumption = getEffectiveConsumption(billData);
                      const monthlyConsumption = effectiveConsumption.effectiveMonthlyConsumption;
                      const yearlyConsumption = effectiveConsumption.effectiveYearlyConsumption;
                      const fin = financials ?? { totalCost: 0, paybackPeriod: 0, roi: 0, savingsYear1: 0, savings25Year: 0, monthlyPayment: 0 };
                      const projectNameBase = `Solar_Project_${new Date().toISOString().split('T')[0]}`;
                      const persistPdf = Boolean(persistPdfProjectId && user);
                      const sourcesData: CalculationSourcesData = {
                        projectName: projectNameBase,
                        locationName: cityData.name,
                        latitude: lat,
                        longitude: lon,
                        altitude: cityData.altitude,
                        tilt: location.locations?.[0]?.tilt ?? location.tilt ?? 10,
                        azimuth: location.locations?.[0]?.azimuth ?? location.azimuth ?? 180,
                        mountingType: location.locations?.[0]?.mounting_type ?? location.mountingType ?? 'roof-fixed',
                        surfaceType: location.locations?.[0]?.surface_type ?? location.surfaceType ?? 'asphalt',
                        userType: userType ?? 'residential',
                        monthlyConsumption,
                        yearlyConsumption,
                        baseYearlyConsumption: effectiveConsumption.baseYearlyConsumption,
                        morningConsumptionPct: effectiveConsumption.morningConsumptionPct,
                        effectiveYearlyConsumption: effectiveConsumption.effectiveYearlyConsumption,
                        customTariffSarPerKwh: billData?.customTariffSarPerKwh,
                        gridPhase: location.electrical?.gridPhase,
                        acCableDistanceM: location.electrical?.acCableDistanceM,
                        dcCableDistanceM: location.electrical?.dcCableDistanceM,
                        design: {
                          systemSize: design.systemSize,
                          panelCount: design.panelCount,
                          yearlyProduction: design.yearlyProduction,
                          offset:
                            effectiveConsumption.effectiveYearlyConsumption > 0
                              ? Math.min(100, Math.round((design.yearlyProduction / effectiveConsumption.effectiveYearlyConsumption) * 100))
                              : 0,
                          initialSystemSize: design.initialSystemSize,
                          inverterAcCapKw: design.inverterAcCapKw,
                          selectedInverterAcKw: design.selectedInverterAcKw,
                          selectedInverterCount: design.selectedInverterCount,
                          dcAcRatioFinal: design.dcAcRatioFinal,
                          monthlyResults: design.monthlyResults,
                        },
                        calculationResults: null,
                        financials: {
                          totalCost: fin.totalCost,
                          paybackPeriod: fin.paybackPeriod,
                          roi: fin.roi,
                          savingsYear1: fin.savingsYear1,
                          savings25Year: fin.savings25Year,
                          monthlyPayment: fin.monthlyPayment,
                        },
                        boq: boq ?? null,
                        selectedModule: null,
                        selectedInverter: null,
                      };
                      const blob = await generateCalculationSourcesPDF(
                        sourcesData,
                        persistPdf ? { disposition: 'both' } : undefined
                      );
                      if (blob instanceof Blob && persistPdfProjectId && user) {
                        await uploadPdfAndRecord({
                          projectId: persistPdfProjectId,
                          designId: persistPdfDesignId,
                          docType: 'calculation_sources_pdf',
                          blob,
                          storageFileBase: `${projectNameBase}_calc_sources`,
                          filesAppendType: 'calculation_sources_pdf',
                        });
                      }
                    } catch (error) {
                      console.error('Calculation sources PDF error:', error);
                      alert('Failed to generate calculation sources document. Please try again.');
                    }
                  }}
                  className="bg-slate-600 text-white px-8 py-4 rounded-lg hover:bg-slate-700 flex items-center justify-center transition-all transform hover:scale-105 shadow-lg text-lg font-medium"
                >
                  <FileText className="h-6 w-6 mr-3" />
                  Detailed Calculations Report (PDF)
                </button>
              </div>
            </div>
          )}

          {/* Contact Information */}
          {contactData && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <Building2 className="h-6 w-6 mr-2 text-blue-500" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium text-gray-900">{contactData.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900">{contactData.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900">{contactData.phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Account Type</p>
                      <p className="font-medium text-gray-900 capitalize">{contactData.accountType || 'N/A'}</p>
                    </div>
                    {contactData.companyName && (
                      <div>
                        <p className="text-sm text-gray-600">Company Name</p>
                        <p className="font-medium text-gray-900">{contactData.companyName}</p>
                      </div>
                    )}
                    {contactData.crNumber && (
                      <div>
                        <p className="text-sm text-gray-600">CR Number</p>
                        <p className="font-medium text-gray-900">{contactData.crNumber}</p>
                      </div>
                    )}
                    {contactData.websiteUrl && (
                      <div>
                        <p className="text-sm text-gray-600">Website</p>
                        <p className="font-medium text-gray-900">{contactData.websiteUrl}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Energy Profile */}
          {billData && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <Battery className="h-6 w-6 mr-2 text-green-500" />
                Energy Profile
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Avg Monthly Usage</p>
                      <p className="text-2xl font-bold text-green-700">{Math.round(avgMonthlyUsageKwh) || 0} kWh</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Monthly Bill</p>
                      <p className="text-lg font-semibold text-gray-900">SAR {billData.monthlyBill || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Utility Provider</p>
                      <p className="font-medium text-gray-900">{billData.utilityProvider || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Yearly Consumption</p>
                      <p className="text-lg font-semibold text-gray-900">{Math.round(yearlyConsumptionKwh) || 0} kWh</p>
                    </div>
                  </div>
                </div>
                {billData.energyMix && Object.keys(billData.energyMix).length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <p className="text-sm text-gray-600 mb-4">Energy Mix</p>
                    <div className="space-y-2">
                      {Object.entries(billData.energyMix).map(([source, percentage]) => (
                        <div key={source} className="flex justify-between">
                          <span className="text-sm text-gray-700 capitalize">{source.replace('-', ' ')}</span>
                          <span className="text-sm font-medium">{String(percentage)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location Details */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <Home className="h-6 w-6 mr-2 text-red-500" />
              Installation Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="font-medium text-gray-900">{location?.address ?? 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Installation Area</p>
                    <p className="font-medium text-gray-900">{location?.areaInSqMeters ?? 'N/A'} m²</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Coordinates</p>
                    <p className="font-medium text-gray-900">
                      {location?.latitude && location?.longitude 
                        ? `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Mounting Type</p>
                    <p className="font-medium text-gray-900 capitalize">
                      {location?.mountingType ? location.mountingType.replace(/-/g, ' ') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Surface Type</p>
                    <p className="font-medium text-gray-900 capitalize">{location?.surfaceType ?? 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tilt Angle</p>
                    <p className="font-medium text-gray-900">{location?.tilt ?? 'N/A'}°</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Azimuth</p>
                    <p className="font-medium text-gray-900">{location?.azimuth ?? 'N/A'}°</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Energy Consumption (Industrial/Commercial) */}
          {energyConsumptionData && (userType === 'industrial' || userType === 'commercial') && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <LineChart className="h-6 w-6 mr-2 text-purple-500" />
                Energy Consumption Analysis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Total Energy Use</p>
                      <p className="text-2xl font-bold text-purple-700">{energyConsumptionData.totalEnergyUse || 0} kWh</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Analysis Mode</p>
                      <p className="font-medium text-gray-900 capitalize">{energyConsumptionData.mode || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Seasonal Variation</p>
                      <p className="font-medium text-gray-900">{energyConsumptionData.seasonalVariation ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-4">Energy Sources Breakdown</p>
                  <div className="space-y-2">
                    {energyConsumptionData.rows && energyConsumptionData.rows.map((row: any, index: number) => (
                      <div key={index} className="flex justify-between">
                        <span className="text-sm text-gray-700 capitalize">{row.source}</span>
                        <span className="text-sm font-medium">{row.consumption} {row.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Site Operations (Industrial/Commercial) */}
          {siteOperationsData && (userType === 'industrial' || userType === 'commercial') && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <Building2 className="h-6 w-6 mr-2 text-orange-500" />
                Site Operations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Monthly Usage</p>
                      <p className="text-2xl font-bold text-orange-700">{siteOperationsData.monthlyUsage || 0} kWh</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Monthly Bill</p>
                      <p className="text-lg font-semibold text-gray-900">SAR {siteOperationsData.monthlyBill || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Yearly Consumption</p>
                      <p className="text-lg font-semibold text-gray-900">{siteOperationsData.yearlyConsumption || 0} kWh</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Operating Hours</p>
                      <p className="font-medium text-gray-900">
                        {siteOperationsData.operatingHours ? 'Configured' : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Selected Utilities</p>
                      <p className="font-medium text-gray-900">
                        {siteOperationsData.selectedUtilities?.length || 0} provider(s)
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">File Uploads</p>
                      <p className="font-medium text-gray-900">
                        {Object.keys(siteOperationsData.fileUploads || {}).length} file(s)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Agricultural Design Requirements */}
          {agriDesignData && userType === 'agricultural' && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <Leaf className="h-6 w-6 mr-2 text-green-500" />
                Agricultural Design Requirements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Daily Water Need</p>
                      <p className="text-2xl font-bold text-green-700">{agriDesignData.waterDemand?.daily_water_need || 0} L/day</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Helper Area</p>
                      <p className="font-medium text-gray-900">{agriDesignData.waterDemand?.helper_area || 0} m²</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Helper Hours</p>
                      <p className="font-medium text-gray-900">{agriDesignData.waterDemand?.helper_hours || 0} hours</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Water Source Type</p>
                      <p className="font-medium text-gray-900 capitalize">{agriDesignData.waterSource?.water_source_type || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Wells Count</p>
                      <p className="font-medium text-gray-900">{agriDesignData.waterSource?.wells_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Pumps Count</p>
                      <p className="font-medium text-gray-900">{agriDesignData.pumpConfiguration?.pumps_count || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Project Summary */}
          <div className="mb-10">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
              <CheckCircle className="h-6 w-6 mr-2 text-green-500" />
              Project Summary
            </h3>
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-1" />
                <div>
                  <h4 className="text-lg font-semibold text-green-800 mb-2">Project Information Complete</h4>
                  <p className="text-green-700 mb-4">
                    Thank you for providing all the necessary information for your {userType || 'solar'} project. 
                    Our team will review your requirements and contact you within 24-48 hours to discuss the next steps.
                  </p>
                  <div className="space-y-2 text-sm text-green-700">
                    <p>• We'll analyze your energy consumption and location requirements</p>
                    <p>• Our engineering team will prepare a customized solar solution</p>
                    <p>• You'll receive a detailed proposal with system specifications and pricing</p>
                    <p>• We'll schedule a site visit to finalize the installation plan</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BOQ (Bill of Quantities) */}
          {boq && (
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
                <TrendingUp className="h-6 w-6 mr-2 text-blue-600" />
                Bill of Quantities (BOQ)
              </h3>
              <BOQDisplay boq={boq} systemSizeKw={design?.systemSize} />
            </div>
          )}

          {/* Company Information */}
          <div className="border-t pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center mb-4 md:mb-0">
                <BrandLogo className="h-9 w-auto" />
              </div>
              <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
                <a href="tel:+920005469" className="flex items-center text-gray-600 hover:text-blue-600">
                  <Home className="h-5 w-5 mr-2" />
                  920005469
                </a>
                <a href="mailto:info@gasable.com" className="flex items-center text-gray-600 hover:text-blue-600">
                  <Building2 className="h-5 w-5 mr-2" />
                  info@gasable.com
                </a>
                <div className="flex items-center text-gray-600">
                  <Leaf className="h-5 w-5 mr-2" />
                    Abu Bakr Al Siddiq Road – Al Masif, Riyadh - Saudi Arabia
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        {onBack && (
          <button
            onClick={onBack}
            className="bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        )}
        
        <button
          onClick={onConfirm}
          className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-8 rounded-lg hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg flex items-center"
        >
          Submit Project Request
          <CheckCircle className="h-5 w-5 ml-2" />
        </button>
      </div>
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}
